import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { C } from '../../constants';
import { CheckCircleIcon } from '../components/AdminIcons';

const SERVICES = ['Eye Correctness Test', 'Frame Selection', 'Prescription Lens Replacement', "Children's Eyewear", 'Sunglasses', 'General Enquiry'];

const INP = {
  width: '100%', padding: '11px 14px',
  border: '1px solid rgba(26,68,128,0.15)',
  borderRadius: '6px', fontSize: '0.88rem',
  fontFamily: 'Jost, sans-serif', color: '#1C2B3A',
  outline: 'none', background: 'white',
  transition: 'border-color 0.2s',
};

const LBL = {
  fontSize: '0.72rem', color: '#4A5568',
  letterSpacing: '0.08em', textTransform: 'uppercase',
  display: 'block', marginBottom: '6px', fontWeight: 500,
};

export default function AddBooking({ setPage }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: '', date: '', message: '', status: 'pending' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const change = e => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.phone || !form.service) {
      setError('Name, phone, and service are required.'); return;
    }
    setSaving(true); setError('');
    try {
      await addDoc(collection(db, 'bookings'), {
        ...form,
        createdAt: serverTimestamp(),
        source: 'manual',
      });
      setDone(true);
      setForm({ name: '', phone: '', email: '', service: '', date: '', message: '', status: 'pending' });
    } catch (e) {
      setError('Failed to save. Check your Firebase connection.');
    }
    setSaving(false);
  };

  if (done) {
    return (
      <div className="admin-page" style={{ padding: "32px", fontFamily: 'Jost, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ color: '#059669', marginBottom: '16px' }}><CheckCircleIcon s={56} /></div>
          <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', fontWeight: 700, color: '#0B1D3A', marginBottom: '8px' }}>Booking Saved!</h2>
          <p style={{ color: '#8899AA', fontSize: '0.9rem', marginBottom: '28px' }}>The appointment has been added to the database successfully.</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button onClick={() => setDone(false)} style={{ padding: '11px 24px', background: C.navy, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Jost, sans-serif', fontSize: '0.84rem', fontWeight: 500 }}>Add Another</button>
            <button onClick={() => setPage('appointments')} style={{ padding: '11px 24px', background: 'white', color: C.navy, border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', fontFamily: 'Jost, sans-serif', fontSize: '0.84rem' }}>View All Appointments</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" style={{ padding: "32px", fontFamily: 'Jost, sans-serif', maxWidth: '720px' }}>
      <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 700, color: '#0B1D3A', marginBottom: '4px' }}>Add Booking</h1>
      <p style={{ color: '#8899AA', fontSize: '0.88rem', marginBottom: '32px' }}>Manually enter a client appointment into the system.</p>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: '6px', padding: '12px 16px', marginBottom: '20px', color: '#DC2626', fontSize: '0.84rem' }}>{error}</div>
      )}

      <div style={{ background: 'white', borderRadius: '10px', padding: '32px', border: '1px solid rgba(26,68,128,0.08)', boxShadow: '0 2px 12px rgba(26,68,128,0.04)' }}>
        {/* Row 1 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={LBL}>Full Name *</label>
            <input name="name" value={form.name} onChange={change} placeholder="Client's full name" style={INP}
              onFocus={e => e.target.style.borderColor = C.blue} onBlur={e => e.target.style.borderColor = 'rgba(26,68,128,0.15)'} />
          </div>
          <div>
            <label style={LBL}>Phone Number *</label>
            <input name="phone" value={form.phone} onChange={change} placeholder="+233..." style={INP}
              onFocus={e => e.target.style.borderColor = C.blue} onBlur={e => e.target.style.borderColor = 'rgba(26,68,128,0.15)'} />
          </div>
        </div>

        {/* Row 2 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={LBL}>Email Address</label>
          <input name="email" value={form.email} onChange={change} placeholder="client@email.com" type="email" style={INP}
            onFocus={e => e.target.style.borderColor = C.blue} onBlur={e => e.target.style.borderColor = 'rgba(26,68,128,0.15)'} />
        </div>

        {/* Row 3 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={LBL}>Service *</label>
            <select name="service" value={form.service} onChange={change} style={{ ...INP, appearance: 'none', cursor: 'pointer' }}
              onFocus={e => e.target.style.borderColor = C.blue} onBlur={e => e.target.style.borderColor = 'rgba(26,68,128,0.15)'}>
              <option value="">Select service...</option>
              {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={LBL}>Appointment Date</label>
            <input name="date" value={form.date} onChange={change} type="date" style={INP}
              onFocus={e => e.target.style.borderColor = C.blue} onBlur={e => e.target.style.borderColor = 'rgba(26,68,128,0.15)'} />
          </div>
        </div>

        {/* Row 4 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={LBL}>Status</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            {['pending', 'completed', 'cancelled'].map(s => (
              <button key={s} type="button" onClick={() => setForm({ ...form, status: s })} style={{
                padding: '8px 18px', borderRadius: '6px', border: '1px solid',
                borderColor: form.status === s ? C.blue : 'rgba(26,68,128,0.15)',
                background: form.status === s ? C.blue : 'white',
                color: form.status === s ? 'white' : '#4A5568',
                cursor: 'pointer', fontSize: '0.8rem', textTransform: 'capitalize',
                fontFamily: 'Jost, sans-serif',
              }}>{s}</button>
            ))}
          </div>
        </div>

        {/* Row 5 */}
        <div style={{ marginBottom: '28px' }}>
          <label style={LBL}>Notes / Message</label>
          <textarea name="message" value={form.message} onChange={change} rows={3} placeholder="Any notes about this appointment..."
            style={{ ...INP, resize: 'vertical', minHeight: '80px' }}
            onFocus={e => e.target.style.borderColor = C.blue} onBlur={e => e.target.style.borderColor = 'rgba(26,68,128,0.15)'} />
        </div>

        <button onClick={submit} disabled={saving} style={{
          padding: '13px 32px', background: saving ? 'rgba(26,68,128,0.5)' : C.navy,
          color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'default' : 'pointer',
          fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
          fontFamily: 'Jost, sans-serif',
        }}>
          {saving ? 'Saving...' : 'Save Appointment'}
        </button>
      </div>
    </div>
  );
}
